import React, { useEffect, useState } from 'react';
import { Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import { usePendingFilterSelection } from '../../../hooks/usePendingFilterSelection';
import {
  FILTER_BUTTON_CLASS,
  DateRangeFilter,
  FilterChip,
  FilterChips,
  FilterLabel,
  FilterOption,
  MultiSelectFilter,
  SegmentedControl,
  SingleSelectFilter,
  ToggleFilter,
  selectedNames,
} from '../agedInventory/AgedInventoryFilters.component';
import { matchesClassQuery, toLocalIsoDate } from '../topSellers/TopSellersReport.component';
import {
  ON_ORDER_NO_DATE_NOTE,
  ON_ORDER_OTB_SPAN_NOTE,
  ON_ORDER_SPAN_OPTIONS,
  getSpanDateRangeInfo,
} from '../vendorPerformance/VendorPerformanceReport.component';
import {
  CORE_ITEMS_OPTIONS,
  CUSTOM_DATE_RANGE,
  DATE_RANGE_PRESETS,
  DEFAULT_FILTERS,
  HIERARCHY_OPTIONS,
  MAX_WOS_OVERRIDE,
  MIN_WOS_OVERRIDE,
  STATUS_OPTIONS,
  WITH_OTB_HIERARCHY_OPTIONS,
  WITHOUT_OTB_HIERARCHY_OPTIONS,
  parseWosOverride,
} from './coreReplenishment.logic';
import type {
  CoreItemsFilter,
  CoreReplenishmentControls,
  CoreReplenishmentFilterState,
  CoreReplenishmentHierarchy,
  OnOrderSpanKey,
  ReplenishmentStatus,
  SkuGrain,
  ValueBasis,
} from './coreReplenishment.types';

const POPOVER_CLASS = 'absolute left-0 top-full mt-1.5 z-50 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl animate-in fade-in zoom-in-95 duration-100';
const OPTION_CLASS =
  'w-full px-3 py-2 text-[13px] font-semibold rounded-[9px] flex items-center justify-between text-left transition-colors cursor-pointer leading-[21px]';
const optionStateClass = (isSelected: boolean) => (isSelected ? 'bg-[#F4F0FF] text-[#7F56D9]' : 'text-[#344054] hover:bg-[#F9FAFB]');
const DATE_INPUT_CLASS =
  'w-full px-2.5 py-1.5 text-[12px] font-medium text-[#344054] border border-[#D0D5DD] rounded-[8px] focus:outline-none focus:ring-1 focus:ring-[#7F56D9]';

export const INFO = {
  dateRange:
    'Sales window for the sales and margin columns only. Velocity, status and reorder use the velocity lookback in Report settings. Custom ends yesterday at the latest.',
  location: 'Stores to include. Products appear with only the SKUs these stores hold.',
  category: 'Merchandise categories to include.',
  class: 'Plan classes to include.',
  vendor: 'Vendors to include. OTB is the class plan, so it is not narrowed by vendor.',
  status: 'Order now, Next order, Covered or Insufficient data, from when the item runs out vs lead time + cadence.',
  coreItems: 'Detected: sold in N of the last M weeks. Pinned: marked core with the pin. Unpinned items are left out of Detected.',
  span: `How far ahead OTB and on-order look. ${ON_ORDER_OTB_SPAN_NOTE}`,
  values: 'Shows the dollar columns at retail or at cost.',
  showSkus: 'Product lists one row per product. SKU adds a row per size and color.',
  wosTarget: `Weeks of supply the reorder covers. Type a whole number from ${MIN_WOS_OVERRIDE} to ${MAX_WOS_OVERRIDE} to override; clear it to return to the plan.`,
  hideCovered: 'Hides items with status Covered.',
  hideZeroSales: 'Hides SKUs with no sales in the date range. SKU level only; product totals do not change.',
  hierarchy: 'Choose how rows are grouped: by class, category, vendor or location. OTB is planned by class, so groupings led by class or category, and Total, also show open-to-buy.',
} as const;

const VALUE_OPTIONS = [
  { value: 'retail', label: 'Retail' },
  { value: 'cost', label: 'Cost' },
] as const;

const SHOW_SKUS_OPTIONS = [
  { value: 'product', label: 'Product' },
  { value: 'sku', label: 'SKU' },
] as const;

const STATUS_FILTER_OPTIONS: FilterOption[] = STATUS_OPTIONS.map((s) => ({
  code: s.key,
  name: s.label,
}));

export const toClassOption = (option: { code: string; name: string }): FilterOption => ({
  code: option.code,
  name: option.code && option.code !== option.name ? `${option.code} ${option.name}` : option.name,
});

/** Popover open state with click-outside closing, scoped to `[data-popover-container]`. */
export function usePopover() {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement | null)?.closest('[data-popover-container]')) setActive(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return {
    active,
    isOpen: (name: string) => active === name,
    open: (name: string) => setActive(name),
    toggle: (name: string) => setActive((open) => (open === name ? null : name)),
    close: () => setActive(null),
  };
}

// --- On-order & OTB span -------------------------------------------------------------------------

interface SpanFilterProps {
  value: OnOrderSpanKey;
  spanStart: string;
  spanEnd: string;
  isOpen: boolean;
  onToggle: () => void;
  onChange: (span: OnOrderSpanKey, start?: string, end?: string) => void;
}

export const SpanFilter: React.FC<SpanFilterProps> = ({ value, spanStart, spanEnd, isOpen, onToggle, onChange }) => {
  const [showCustom, setShowCustom] = useState(false);
  const [draftStart, setDraftStart] = useState(spanStart);
  const [draftEnd, setDraftEnd] = useState(spanEnd);
  const today = toLocalIsoDate(new Date());
  const current = getSpanDateRangeInfo(value, new Date(), spanStart, spanEnd);

  return (
    <div className="flex flex-col gap-1 relative min-w-[150px]" data-popover-container>
      <FilterLabel label="On-order and OTB span" info={`${INFO.span} ${ON_ORDER_NO_DATE_NOTE}`} />
      <button
        type="button"
        onClick={() => {
          setDraftStart(spanStart);
          setDraftEnd(spanEnd);
          setShowCustom(value === 'custom');
          onToggle();
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={FILTER_BUTTON_CLASS}
      >
        <span className="truncate">{value === 'custom' ? current.dateRange : current.label}</span>
        <ChevronDown className="w-3.5 h-3.5 text-[#667085] flex-shrink-0" />
      </button>
      {isOpen && (
        <div role="listbox" aria-label="On-order and OTB span" className={`${POPOVER_CLASS} w-72 p-2.5 flex flex-col gap-1`}>
          {ON_ORDER_SPAN_OPTIONS.map((span) => {
            const info = getSpanDateRangeInfo(span);
            const isSelected = span === value && !showCustom;
            return (
              <button
                key={span}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => onChange(span)}
                className={`${OPTION_CLASS} ${optionStateClass(isSelected)}`}
              >
                <span className="flex flex-col">
                  <span>{info.label}</span>
                  <span className="text-[12px] font-normal text-[#667085]">{info.dateRange}</span>
                </span>
                {isSelected && <Check className="w-4 h-4 text-[#7F56D9]" />}
              </button>
            );
          })}
          <button
            type="button"
            role="option"
            aria-selected={showCustom}
            onClick={() => setShowCustom(true)}
            className={`${OPTION_CLASS} ${optionStateClass(showCustom)}`}
          >
            <span>Custom Range</span>
            {showCustom && <Check className="w-4 h-4 text-[#7F56D9]" />}
          </button>
          {showCustom && (
            <div className="mt-2 pt-2.5 border-t border-[#F2F4F7] flex flex-col gap-2">
              <label className="flex flex-col gap-1 text-[11px] font-semibold text-[#475467]">
                From
                <input
                  type="date"
                  min={today}
                  max={draftEnd || undefined}
                  value={draftStart}
                  onChange={(e) => setDraftStart(e.target.value)}
                  className={DATE_INPUT_CLASS}
                />
              </label>
              <label className="flex flex-col gap-1 text-[11px] font-semibold text-[#475467]">
                To
                <input type="date" min={draftStart || today} value={draftEnd} onChange={(e) => setDraftEnd(e.target.value)} className={DATE_INPUT_CLASS} />
              </label>
              <button
                type="button"
                onClick={() => onChange('custom', draftStart, draftEnd)}
                disabled={!draftStart || !draftEnd || draftEnd < draftStart}
                className="w-full mt-1 px-3 py-2 bg-[#7F56D9] text-white text-[12px] font-semibold rounded-[8px] hover:bg-[#6941C6] disabled:opacity-50 transition-colors cursor-pointer"
              >
                Apply Range
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Weeks of supply target ----------------------------------------------------------------------

interface WosTargetInputProps {
  value: number | null;
  // "plan × 1.25": where the target comes from while no override is set.
  planLabel: string;
  // The plan's target in weeks ("5.8", or a range across classes), shown while no override is set.
  planValue: string | null;
  onChange: (value: number | null) => void;
}

export const WosTargetInput: React.FC<WosTargetInputProps> = ({ value, planLabel, planValue, onChange }) => {
  const [text, setText] = useState(value === null ? '' : String(value));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setText(value === null ? '' : String(value));
    setError(null);
  }, [value]);

  const commit = () => {
    const trimmed = text.trim();
    if (trimmed === '') {
      setError(null);
      if (value !== null) onChange(null);
      return;
    }
    const parsed = parseWosOverride(trimmed);
    if (parsed === null) {
      setError(`Whole number ${MIN_WOS_OVERRIDE}–${MAX_WOS_OVERRIDE}`);
      return;
    }
    setError(null);
    if (parsed !== value) onChange(parsed);
  };

  return (
    <div className="flex flex-col gap-1 relative">
      <FilterLabel label="Weeks of supply target" info={INFO.wosTarget} />
      <div
        className={`h-[34px] w-[170px] flex items-center gap-1.5 bg-white border rounded-[9px] pl-2.5 pr-1 shadow-2xs focus-within:ring-2 focus-within:ring-[#7F56D9] ${
          error ? 'border-[#FDA29B]' : 'border-[#D0D5DD]'
        }`}
      >
        {/* While empty the plan's value shows as the placeholder, styled like a typed value. */}
        <input
          type="text"
          inputMode="numeric"
          value={text}
          placeholder={planValue ?? ''}
          aria-label="Weeks of supply target"
          aria-invalid={error !== null}
          onChange={(e) => setText(e.target.value.replace(/[^\d]/g, ''))}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setText(value === null ? '' : String(value));
          }}
          className="w-full min-w-0 text-[13px] font-semibold text-[#344054] placeholder:text-[#344054] placeholder:font-semibold focus:outline-none bg-transparent"
        />
        {value === null ? (
          <span className="shrink-0 px-1.5 py-0.5 rounded-md bg-[#F2F4F7] text-[11px] font-medium text-[#667085] whitespace-nowrap">{planLabel}</span>
        ) : (
          <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-[#F4F0FF] text-[11px] font-medium text-[#6941C6] whitespace-nowrap">
            override
            <button
              type="button"
              onClick={() => onChange(null)}
              aria-label="Clear weeks of supply target"
              className="p-0.5 text-[#9E77ED] hover:text-[#53389E] cursor-pointer"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        )}
      </div>
      {error && (
        <span role="alert" className="absolute top-full mt-0.5 text-[11px] text-[#B42318] whitespace-nowrap">
          {error}
        </span>
      )}
    </div>
  );
};

// --- Hierarchy (below the card) ------------------------------------------------------------------

interface HierarchySelectProps {
  value: CoreReplenishmentHierarchy;
  isOpen: boolean;
  onToggle: () => void;
  onChange: (value: CoreReplenishmentHierarchy) => void;
}

const HIERARCHY_SECTIONS = [
  { title: 'With open-to-buy', options: WITH_OTB_HIERARCHY_OPTIONS },
  { title: 'Without open-to-buy', options: WITHOUT_OTB_HIERARCHY_OPTIONS },
];

const OtbPill: React.FC = () => <span className="px-1.5 rounded-md bg-[#F4F0FF] text-[#7F56D9] text-[11px] font-bold">OTB</span>;

export const HierarchySelect: React.FC<HierarchySelectProps> =({ value, isOpen, onToggle, onChange }) => {
  const selected = HIERARCHY_OPTIONS.find((opt) => opt.key === value);
  return (
    <div className="flex flex-col gap-1 relative min-w-[190px]" data-popover-container>
      <FilterLabel label="Hierarchy" info={INFO.hierarchy} />
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={isOpen ? FILTER_BUTTON_CLASS.replace('border-[#D0D5DD]', 'border-[#9E77ED] ring-2 ring-[#F4EBFF]') : FILTER_BUTTON_CLASS}
      >
        <span className="truncate">{selected?.label ?? HIERARCHY_OPTIONS[0].label}</span>
        {selected?.hasOtb && <OtbPill />}
        {isOpen ? (
          <ChevronUp className="w-3.5 h-3.5 text-[#667085] flex-shrink-0 ml-auto" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-[#667085] flex-shrink-0 ml-auto" />
        )}
      </button>
      {isOpen && (
        <div role="listbox" aria-label="Hierarchy" className={`${POPOVER_CLASS} w-72 p-2.5 flex flex-col gap-1`}>
          {HIERARCHY_SECTIONS.map((section) => (
            <div key={section.title} className="flex flex-col gap-0.5">
              <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[#71717A]">{section.title}</div>
              {section.options.map((opt) => {
                const isSelected = opt.key === value;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => onChange(opt.key)}
                    className={`${OPTION_CLASS} ${optionStateClass(isSelected)}`}
                  >
                    <span>{opt.label}</span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      {opt.hasOtb && <OtbPill />}
                      {isSelected && <Check className="w-4 h-4 text-[#7F56D9]" />}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- Control card --------------------------------------------------------------------------------

export interface CoreReplenishmentFilterOptions {
  locations: FilterOption[];
  categories: FilterOption[];
  classes: FilterOption[];
  vendors: FilterOption[];
}

export interface CoreReplenishmentFiltersProps {
  filters: CoreReplenishmentFilterState;
  onFiltersChange: (changes: Partial<CoreReplenishmentFilterState>) => void;
  controls: CoreReplenishmentControls;
  onControlsChange: (changes: Partial<CoreReplenishmentControls>) => void;
  options: CoreReplenishmentFilterOptions;
  hasOtb: boolean;
  // "plan × 1.25" and the plan's weeks ("5.8"), shown in the target field while it has no override.
  planTargetLabel: string;
  planTargetValue: string | null;
  // Resolved date range, for the chip.
  dateRangeLabel: string;
  // Clear all: filters only, not hierarchy, span or values.
  onClearFilters: () => void;
}

/** The control card: every filter and toggle. Hierarchy and the search tools sit below it. */
export const CoreReplenishmentFilters: React.FC<CoreReplenishmentFiltersProps> = ({
  filters,
  onFiltersChange,
  controls,
  onControlsChange,
  options,
  hasOtb,
  planTargetLabel,
  planTargetValue,
  dateRangeLabel,
  onClearFilters,
}) => {
  const popover = usePopover();

  const locationDraft = usePendingFilterSelection({ codes: filters.locations, exclude: filters.excludeLocations }, popover.isOpen('location'), (next) =>
    onFiltersChange({
      locations: next.codes,
      excludeLocations: next.exclude,
    })
  );
  const categoryDraft = usePendingFilterSelection({ codes: filters.categories, exclude: filters.excludeCategories }, popover.isOpen('category'), (next) =>
    onFiltersChange({
      categories: next.codes,
      excludeCategories: next.exclude,
    })
  );
  const classDraft = usePendingFilterSelection({ codes: filters.classes, exclude: filters.excludeClasses }, popover.isOpen('class'), (next) =>
    onFiltersChange({ classes: next.codes, excludeClasses: next.exclude })
  );
  const vendorDraft = usePendingFilterSelection({ codes: filters.vendors, exclude: filters.excludeVendors }, popover.isOpen('vendor'), (next) =>
    onFiltersChange({ vendors: next.codes, excludeVendors: next.exclude })
  );
  const statusDraft = usePendingFilterSelection({ codes: filters.statuses, exclude: false }, popover.isOpen('status'), (next) =>
    onFiltersChange({ statuses: next.codes as ReplenishmentStatus[] })
  );

  const entityChip = (
    key: string,
    label: string,
    codes: string[],
    exclude: boolean,
    list: FilterOption[],
    clear: Partial<CoreReplenishmentFilterState>
  ): FilterChip | null =>
    codes.length === 0
      ? null
      : {
          key,
          label: `${label}: ${exclude ? 'not ' : ''}${selectedNames(codes, list).join(', ')}`,
          onRemove: () => onFiltersChange(clear),
        };

  const chips: FilterChip[] = [
    filters.dateRange !== DEFAULT_FILTERS.dateRange
      ? {
          key: 'date',
          label: `Date range: ${dateRangeLabel}`,
          onRemove: () =>
            onFiltersChange({
              dateRange: DEFAULT_FILTERS.dateRange,
              customStartDate: '',
              customEndDate: '',
            }),
        }
      : null,
    entityChip('location', 'Location', filters.locations, filters.excludeLocations, options.locations, { locations: [], excludeLocations: false }),
    entityChip('category', 'Category', filters.categories, filters.excludeCategories, options.categories, { categories: [], excludeCategories: false }),
    entityChip('class', 'Class', filters.classes, filters.excludeClasses, options.classes, { classes: [], excludeClasses: false }),
    entityChip('vendor', 'Vendor', filters.vendors, filters.excludeVendors, options.vendors, { vendors: [], excludeVendors: false }),
    entityChip('status', 'Status', filters.statuses, false, STATUS_FILTER_OPTIONS, { statuses: [] }),
    filters.coreItems !== DEFAULT_FILTERS.coreItems
      ? {
          key: 'core',
          label: `Core items: ${CORE_ITEMS_OPTIONS.find((o) => o.key === filters.coreItems)?.label}`,
          onRemove: () => onFiltersChange({ coreItems: DEFAULT_FILTERS.coreItems }),
        }
      : null,
    controls.hideCovered
      ? {
          key: 'hide_covered',
          label: 'Hide covered',
          onRemove: () => onControlsChange({ hideCovered: false }),
        }
      : null,
    controls.hideZeroSalesSkus && controls.skuGrain === 'sku'
      ? {
          key: 'hide_zero',
          label: 'Hide zero-sales SKUs',
          onRemove: () => onControlsChange({ hideZeroSalesSkus: false }),
        }
      : null,
  ].filter((chip): chip is FilterChip => chip !== null);

  return (
    <div className="bg-white p-5 rounded-[16px] border border-[#E4E4E7] shadow-2xs relative z-30 flex flex-col gap-4" data-testid="core-replenishment-filters">
      <div className="flex flex-wrap items-end gap-3">
        <DateRangeFilter
          info={INFO.dateRange}
          presets={DATE_RANGE_PRESETS}
          customPreset={CUSTOM_DATE_RANGE}
          value={filters.dateRange}
          customStart={filters.customStartDate}
          customEnd={filters.customEndDate}
          isOpen={popover.isOpen('date_range')}
          onToggle={() => popover.toggle('date_range')}
          onPreset={(preset) => {
            onFiltersChange({
              dateRange: preset,
              customStartDate: '',
              customEndDate: '',
            });
            popover.close();
          }}
          onCustom={(start, end) => {
            onFiltersChange({
              dateRange: CUSTOM_DATE_RANGE,
              customStartDate: start,
              customEndDate: end,
            });
            popover.close();
          }}
        />
        <MultiSelectFilter
          label="Location"
          allLabel="All locations"
          info={INFO.location}
          noun="location"
          pluralNoun="locations"
          options={options.locations}
          appliedCodes={filters.locations}
          appliedExclude={filters.excludeLocations}
          draft={locationDraft}
          isOpen={popover.isOpen('location')}
          onToggle={() => popover.toggle('location')}
          onClose={popover.close}
        />
        <MultiSelectFilter
          label="Category"
          allLabel="All categories"
          info={INFO.category}
          noun="category"
          pluralNoun="categories"
          options={options.categories}
          appliedCodes={filters.categories}
          appliedExclude={filters.excludeCategories}
          draft={categoryDraft}
          isOpen={popover.isOpen('category')}
          onToggle={() => popover.toggle('category')}
          onClose={popover.close}
        />
        <MultiSelectFilter
          label="Class"
          allLabel="All classes"
          info={INFO.class}
          noun="class"
          pluralNoun="classes"
          options={options.classes}
          appliedCodes={filters.classes}
          appliedExclude={filters.excludeClasses}
          draft={classDraft}
          isOpen={popover.isOpen('class')}
          onToggle={() => popover.toggle('class')}
          onClose={popover.close}
          matches={(option, query) => matchesClassQuery(option, query)}
        />
        <MultiSelectFilter
          label="Vendor"
          allLabel="All vendors"
          info={INFO.vendor}
          noun="vendor"
          pluralNoun="vendors"
          options={options.vendors}
          appliedCodes={filters.vendors}
          appliedExclude={filters.excludeVendors}
          draft={vendorDraft}
          isOpen={popover.isOpen('vendor')}
          onToggle={() => popover.toggle('vendor')}
          onClose={popover.close}
        />
        <MultiSelectFilter
          label="Status"
          allLabel="All statuses"
          info={INFO.status}
          noun="status"
          pluralNoun="statuses"
          options={STATUS_FILTER_OPTIONS}
          appliedCodes={filters.statuses}
          appliedExclude={false}
          draft={statusDraft}
          isOpen={popover.isOpen('status')}
          onToggle={() => popover.toggle('status')}
          onClose={popover.close}
          allowExclude={false}
          searchable={false}
        />
        <SingleSelectFilter<CoreItemsFilter>
          label="Core items"
          info={INFO.coreItems}
          value={filters.coreItems}
          options={CORE_ITEMS_OPTIONS.map((o) => ({
            value: o.key,
            label: o.label,
          }))}
          isOpen={popover.isOpen('core_items')}
          onToggle={() => popover.toggle('core_items')}
          onChange={(value) => {
            onFiltersChange({ coreItems: value });
            popover.close();
          }}
        />
        {hasOtb && (
          <SpanFilter
            value={controls.onOrderSpan}
            spanStart={controls.spanStart}
            spanEnd={controls.spanEnd}
            isOpen={popover.isOpen('span')}
            onToggle={() => popover.toggle('span')}
            onChange={(span, start = '', end = '') => {
              onControlsChange({
                onOrderSpan: span,
                spanStart: start,
                spanEnd: end,
              });
              popover.close();
            }}
          />
        )}
        <SegmentedControl<ValueBasis>
          label="Values"
          info={INFO.values}
          value={controls.valueBasis}
          options={VALUE_OPTIONS}
          onChange={(valueBasis) => onControlsChange({ valueBasis })}
        />
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <SegmentedControl<SkuGrain>
          label="Show SKUs"
          info={INFO.showSkus}
          value={controls.skuGrain}
          options={SHOW_SKUS_OPTIONS}
          onChange={(skuGrain) => onControlsChange({ skuGrain })}
        />
        <WosTargetInput
          value={controls.wosOverride}
          planLabel={planTargetLabel}
          planValue={planTargetValue}
          onChange={(wosOverride) => onControlsChange({ wosOverride })}
        />
        <ToggleFilter
          label="Hide covered"
          info={INFO.hideCovered}
          checked={controls.hideCovered}
          onChange={(hideCovered) => onControlsChange({ hideCovered })}
        />
        {/* SKU rows only, so it shows with the SKU view. */}
        {controls.skuGrain === 'sku' && (
          <ToggleFilter
            label="Hide zero-sales SKUs"
            info={INFO.hideZeroSales}
            checked={controls.hideZeroSalesSkus}
            onChange={(hideZeroSalesSkus) => onControlsChange({ hideZeroSalesSkus })}
          />
        )}
      </div>

      <FilterChips chips={chips} onClearAll={onClearFilters} />
    </div>
  );
};

export default CoreReplenishmentFilters;
